import { useCallback, useEffect, useState } from "react";
import { type API, type TextResponse } from "../logic/classApi";
import {
  createUrl,
  formatApiDateString,
  formatApiTimeString,
  formatApiYmdString,
} from "./apiCommon";
import { showErrorMessage, showSuccessMessage } from "../components/appPane";
import { MESSAGES } from "../constants/const";
import type { Chemical, ChemicalType, ChemicalVendor } from "../types";

/** 発注書出力APIのURL */
const API_DOWNLOAD_ORDER_URL = import.meta.env.VITE_API_DOWNLOAD_ORDER_URL;

/** 納入実績表出力APIのURL */
const API_DOWNLOAD_ORDERRESULT_URL = import.meta.env.VITE_API_DOWNLOAD_ORDERRESULT_URL;

/** 年間調達計画APIのURL */
const API_DOWNLOAD_ORDERPLAN_URL = import.meta.env.VITE_API_DOWNLOAD_ORDERPLAN_URL;

/**
 * 帳票出力API
 * @param api APIモジュール
 * @param branchCode 支所コード
 * @returns
 */
export function useDownloadApi(api: API, branchCode: string) {
  /**
   * バイナリ（Base64文字列）のBlob変換
   * @param base64String Base64文字列
   * @returns Blobオブジェクト
   */
  const base64ToBlob = (base64String: string): Blob => {
    // 余分な空白/改行/両端のダブルクォート/data:URL前置きを除去
    const cleaned = base64String.replace(/^data:.*;base64,/, "").replace(/\s/g, "");
    const byteChars = atob(cleaned);
    const len = byteChars.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) bytes[i] = byteChars.charCodeAt(i);
    return new Blob([bytes]);
  };

  /**
   * ファイルダウンロード
   * @param name ファイル名
   * @param base64Text バイナリデータ（Base64文字列）
   */
  const download = (name: string, base64Text: string) => {
    const blob = base64ToBlob(base64Text);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = name;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  /**
   * ファイルダウンロードAPI呼び出し共通処理
   * @param name ファイル名
   * @param url APIのURL
   * @param body APIのリクエストボディ
   */
  const procDownload = async (name: string, url: string, body: Object, message: string) => {
    try {
      const { payload: base64Text } = await api.postRequestJson<TextResponse>(url, body);
      await download(name, base64Text);
      showSuccessMessage(message);
    } catch (e) {
      console.error(e);
      showErrorMessage(MESSAGES.ERROR_GET);
    }
  };

  /**
   * 発注書出力API
   * @param name ファイル名
   * @param chemicalId 薬剤IDまたは薬剤ID配列
   * @param scheduledDate 注文日付または注文日付配列
   * @param scheduledTime 注文時刻または注文時刻配列
   * @param scheduledTimeEnd 注文終了時刻または注文終了時刻配列
   * @param orderVolume 発注量または発注量配列
   */
  const downloadOrder = async (
    name: string,
    chemicalId: number | number[],
    scheduledDate: string | string[],
    scheduledTime: string | string[],
    scheduledTimeEnd: string | string[],
    orderVolume: number | number[]
  ) => {
    const body = {
      branchCode,
      chemicalId,
      scheduledDate,
      scheduledTime,
      scheduledTimeEnd,
      orderVolume,
    } as {
      branchCode: string;
      chemicalId: number | number[];
      scheduledDate: string | string[];
      scheduledTime: string | string[];
      scheduledTimeEnd: string | string[];
      orderVolume: number | number[];
    };

    if (!Array.isArray(scheduledDate) && !Array.isArray(scheduledTime) && !Array.isArray(orderVolume)) {
      const date = new Date(`${scheduledDate} ${scheduledTime === "" ? "00:00" : scheduledTime}`);
      body.scheduledDate = formatApiDateString(date);
      body.scheduledTime = formatApiTimeString(date);
    }

    await procDownload(name, API_DOWNLOAD_ORDER_URL, body, MESSAGES.SUCCESS_ORDER_DOWNLOAD);
  };

  /**
   * 納入実績表出力API
   * @param name ファイル名
   * @param fiscalYear 年度
   */
  const downloadOrderResult = async (name: string, fiscalYear: string) => {
    const body = { branchCode, fiscalYear };
    await procDownload(
      name,
      API_DOWNLOAD_ORDERRESULT_URL,
      body,
      MESSAGES.SUCCESS_ORDER_RESULT_DOWNLOAD
    );
  };

  /**
   * 年間調達計画出力API
   * @param name ファイル名
   * @param fiscalYear 年度
   */
  const downloadOrderPlan = async (name: string, fiscalYear: string) => {
    const body = { branchCode, fiscalYear };
    await procDownload(
      name,
      API_DOWNLOAD_ORDERPLAN_URL,
      body,
      MESSAGES.SUCCESS_ORDER_PLAN_DOWNLOAD
    );
  };

  return [{ downloadOrder, downloadOrderResult, downloadOrderPlan }];
}
